import React, { useState, } from "react";
import { FormattedMessage, } from "react-intl";
import moment from 'moment-timezone';
import { StatusBadge, FormatEuro, ProfileImage, MomentWrapper, } from '../../components/helpers';
import { Table, Button, Col, Tooltip, Row, } from 'antd';
import { BookOpenTextIcon, CalendarCheckIcon, CheckCircleIcon, XCircleIcon, AlarmIcon, ExportIcon, } from "@phosphor-icons/react";
import DownloadButton from './invoice.download.button';
import TableHeaderSearch from '../../components/helpers/table.search';
import InvoiceExportModal from './invoice.export.modal';

const InvoiceGridView = ({
    dataLoading = true,
    setDataLoading,
    dataTable,
    refreshData,
    searchInput,
    setSearchInput,
    filteredInfo,
    setFilteredInfo,
    history,
    tableParams,
    setTableParams,
    ...props
}) => {

    const [invoiceExportModalOpen, setInvoiceExportModalOpen] = useState(false);
    const localdata = sessionStorage.getItem('authentication');
    const idToken = JSON.parse(localdata);
    const { customer_id, } = idToken;

    const clickTableRow = (data) => {
        history(`/invoice/${data.invoice_id}`);
    };

    const columns = [
        {
            title: <FormattedMessage id="status" />,
            dataIndex: 'invoice_status',
            key: 'invoice_status',
            align: 'center',
            width: 200,
            render: invoice_status => (
                <StatusBadge status={invoice_status}
                    icon={invoice_status === "PAID" ? <CheckCircleIcon size={14} /> : (invoice_status === "OVER_DUE" ? <AlarmIcon size={14} /> : <XCircleIcon size={14} />)} />
            )
        },
        {
            title: <FormattedMessage id="invoice-number" />,
            dataIndex: 'invoice_number',
            key: 'invoice_number',
            width: 150,
            render: invoice_number => (
                <span>
                    {invoice_number ? invoice_number : 'n/a'}
                </span>
            ),
        },
        {
            title: <FormattedMessage id="customer-name" />,
            dataIndex: 'customer_name',
            key: 'customer_name',
            sorter: (a, b) => { return a.customer_data?.customer_name.localeCompare(b.customer_data?.customer_name); },
            render: (_, record) => (
                <Row align="middle">
                    <Col span={4}>
                        <ProfileImage size="sm" email={record.customer_data?.customer_email} className="rounded-full" />
                    </Col>
                    <Col span={15} className="ml-2">
                        <div className="font-weight-bolder">{record.customer_data?.customer_name}</div>
                        <div className="font-weight-light">{record.customer_data?.customer_email}</div>
                    </Col>
                </Row>
            )
        },
        {
            title: <FormattedMessage id="invoice-date" />,
            dataIndex: 'invoice_date',
            key: 'invoice_date',
            width: 150,
            sorter: (a, b) => moment(a.invoice_date) - moment(b.invoice_date),
            defaultSortOrder: ['descend'],
            render: invoice_date => (<MomentWrapper tz="Europe/Amsterdam" format="DD-MM-YYYY" date={invoice_date} />),
        },
        {
            title: <FormattedMessage id="invoice-totals" />,
            dataIndex: 'invoice_amount',
            key: 'invoice_amount',
            // width: 250,
            render: (invoice_amount, record) => (
                <Row align="middle" justify="space-around">
                    <Col span={24}>
                        <div className="font-weight-bolder">
                            Totaal: <FormatEuro amount={invoice_amount} />
                        </div>
                    </Col>
                    <Col span={24}>
                        <div className="font-weight-light">Netto: <FormatEuro amount={record.invoice_totals.total_netto} /></div>
                        <div className="font-weight-light">BTW: <FormatEuro amount={record.invoice_totals.total_vat} /></div>
                    </Col>
                </Row>
            ),
        },
        {
            title: "",
            dataIndex: "button",
            key: "button",
            align: 'center',
            width: 150,
            render: (button, record) => (
                <>
                    {/* Download invoice */}
                    <DownloadButton
                        size="small"
                        hidden={record.invoice_status === "DRAFT"}
                        invoiceID={record.invoice_id}
                        customerID={record.customer_id}
                        history={history} />

                    {/* Got to linked booking */}
                    {record.customer_id !== customer_id && (
                        <Tooltip placement="top" title={<FormattedMessage id="invoice-order-open-tt" />}>
                            <Button
                                size="small"
                                hidden={record.invoice_status === "DRAFT"}
                                icon={<CalendarCheckIcon size={16} weight="fill" color="#5c5c5c" />}
                                onClick={(event) => { history(`/booking/details/${record.booking_id}`); event.stopPropagation() }}
                                className="mr-1"
                            />
                        </Tooltip>
                    )}

                    {/* Open invoice details */}
                    <Tooltip placement="top" title={<FormattedMessage id="invoice-open-details-tt" />}>
                        <Button
                            size="small"
                            icon={<BookOpenTextIcon size={16} weight="fill" color="#5c5c5c" />}
                            onClick={(event) => { clickTableRow(record); event.stopPropagation() }}
                            className="mr-1"
                        />
                    </Tooltip>
                </>
            )
        }
    ];

    const exportData = () => {
        setInvoiceExportModalOpen(true);
    };


    const handleChange = (pagination, filters, sorter) => {
        setFilteredInfo(filters);

        // `dataSource` is useless since `pageSize` changed
        if (pagination.pageSize !== tableParams.pagination?.pageSize) {
            setDataLoading(true);
            // setInvoiceList([]);
        }

        if (pagination.current !== tableParams.pagination?.current) {
            setDataLoading(true);
            // setInvoiceList([]);
            pagination.presious_page = tableParams.pagination?.current;
        }

        setTableParams({ pagination, filters, ...sorter, });

    };

    return (

        <React.Fragment>

            <Table
                rowKey={record => record.invoice_id}
                onRow={(record, rowIndex) => {
                    return {
                        onClick: event => { clickTableRow(record) }, // click row
                        onDoubleClick: event => { }, // double click row
                        onContextMenu: event => { }, // right button click row
                        onMouseEnter: event => { }, // mouse enter row
                        onMouseLeave: event => { }, // mouse leave row
                    };
                }}
                caption={
                    <TableHeaderSearch
                        searchInput={searchInput}
                        setSearchInput={setSearchInput}
                        setTableLoading={setDataLoading}
                        resetData={refreshData}
                        filteredInfo={filteredInfo}
                        setFilteredInfo={setFilteredInfo}
                        label="invoice-search"
                        buttons={[{
                            key: "export",
                            label: <FormattedMessage id="export" />,
                            onClick: e => exportData(),
                            icon: <ExportIcon size={16} className="mr-1" />,
                        },]}
                    />
                }
                rowClassName="pointer"
                dataSource={dataTable}
                onChange={handleChange}
                pagination={tableParams?.pagination}
                columns={columns}
                loading={dataLoading} />

            {invoiceExportModalOpen && (
                <InvoiceExportModal
                    modalIsOpen={invoiceExportModalOpen}
                    modalIsOpenHandler={setInvoiceExportModalOpen}
                    history={history}
                />
            )}

        </React.Fragment>
    );
};

export default InvoiceGridView;